export type LeadStatus =
  | 'Nieuw'
  | 'Contact gelegd'
  | 'Afspraak gepland'
  | 'Offerte opstellen'
  | 'Offerte verstuurd'
  | 'Gewonnen'
  | 'Verloren';

export type LeadSource =
  | 'Website'
  | 'Verwijzing'
  | 'Telefoon'
  | 'Email'
  | 'Bouwplaatsbezoek'
  | 'Bestaande klant'
  | 'VvE'
  | 'Homedeal'
  | 'Overig';

export type WorkType =
  | 'Schilderwerk buiten'
  | 'Schilderwerk binnen'
  | 'Houtrot herstel'
  | 'Glaswerk'
  | 'Behangwerk'
  | 'Compleet onderhoud'
  | 'Overig';

export type LeadPriority = 'hoog' | 'normaal' | 'laag';

export type LeadActivityType =
  | 'status_change'
  | 'note'
  | 'call'
  | 'email'
  | 'appointment'
  | 'follow_up'
  | 'assignment';

export type AppointmentStatus =
  | 'concept'
  | 'voorgesteld'
  | 'geaccepteerd'
  | 'afgewezen'
  | 'tegenvoorstel'
  | 'voltooid';

export type CommunicationPreference = 'email' | 'whatsapp' | 'both';

export type PrijsType = 'regulier' | 'collectief';

export interface Lead {
  id: string;
  lead_number: string;
  contact_name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  postcode: string | null;
  plaats: string | null;
  lat: number | null;
  lng: number | null;
  contact_id: string | null;
  work_type: WorkType | null;
  work_description: string | null;
  estimated_value: number | null;
  source: LeadSource | null;
  source_detail: string | null;
  status: LeadStatus;
  assigned_planner_id: string | null;
  assigned_adviseur_id: string | null;
  project_id: string | null;
  converted_at: string | null;
  loss_reason: string | null;
  notes: string | null;
  priority: LeadPriority;
  first_contact_at: string | null;
  first_contact_deadline: string | null;
  communication_preference: CommunicationPreference;
  prijs_type: PrijsType;
  is_archived?: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  assigned_planner?: { id: string; full_name: string | null } | null;
  assigned_adviseur?: { id: string; full_name: string | null } | null;
}

export interface LeadShare {
  id: string;
  lead_id: string;
  adviseur_id: string;
  created_by: string | null;
  created_at: string;
}

export interface GevelBewerking {
  id?: string;
  gevel_id: string;
  gebouwdeel: string;
  materiaal: 'Hout' | 'Staal' | 'Steen' | 'Kunststof' | null;
  bewerking: string;
  marker_id: string | null;
  volgorde: number;
}

export interface AiAnalysis {
  id: string;
  gevel_id: string;
  analysis_type: 'gebouwdelen' | 'kleuren' | 'staat';
  result: Record<string, unknown>;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string | null;
  created_at: string;
}

export interface LeadActivity {
  id: string;
  lead_id: string;
  activity_type: LeadActivityType;
  description: string;
  old_status: string | null;
  new_status: string | null;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  creator?: { full_name: string | null } | null;
}

export interface LeadAppointment {
  id: string;
  lead_id: string;
  adviseur_id: string | null;
  proposed_datetime: string;
  end_datetime: string | null;
  status: AppointmentStatus;
  token: string;
  client_response_datetime: string | null;
  client_message: string | null;
  reminder_sent: boolean;
  day_notification_sent: boolean;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  adviseur?: { id: string; full_name: string | null } | null;
  lead?: Lead | null;
}

export const LEAD_STATUSES: LeadStatus[] = [
  'Nieuw',
  'Contact gelegd',
  'Afspraak gepland',
  'Offerte opstellen',
  'Offerte verstuurd',
  'Gewonnen',
  'Verloren',
];

export const LEAD_SOURCES: LeadSource[] = [
  'Website',
  'Verwijzing',
  'Telefoon',
  'Email',
  'Bouwplaatsbezoek',
  'Bestaande klant',
  'VvE',
  'Homedeal',
  'Overig',
];

export const WORK_TYPES: WorkType[] = [
  'Schilderwerk buiten',
  'Schilderwerk binnen',
  'Houtrot herstel',
  'Glaswerk',
  'Behangwerk',
  'Compleet onderhoud',
  'Overig',
];

export const LEAD_STATUS_COLORS: Record<
  LeadStatus,
  { dot: string; bg: string; text: string; border: string }
> = {
  Nieuw: {
    dot: 'bg-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-950',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-800',
  },
  'Contact gelegd': {
    dot: 'bg-cyan-500',
    bg: 'bg-cyan-50 dark:bg-cyan-950',
    text: 'text-cyan-700 dark:text-cyan-300',
    border: 'border-cyan-200 dark:border-cyan-800',
  },
  'Afspraak gepland': {
    dot: 'bg-violet-500',
    bg: 'bg-violet-50 dark:bg-violet-950',
    text: 'text-violet-700 dark:text-violet-300',
    border: 'border-violet-200 dark:border-violet-800',
  },
  'Offerte opstellen': {
    dot: 'bg-orange-500',
    bg: 'bg-orange-50 dark:bg-orange-950',
    text: 'text-orange-700 dark:text-orange-300',
    border: 'border-orange-200 dark:border-orange-800',
  },
  'Offerte verstuurd': {
    dot: 'bg-[#F598AF]',
    bg: 'bg-[#F598AF]/10 dark:bg-[#F598AF]/20',
    text: 'text-[#e0476a] dark:text-[#F598AF]',
    border: 'border-[#F598AF]/30 dark:border-[#F598AF]/40',
  },
  Gewonnen: {
    dot: 'bg-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-950',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
  Verloren: {
    dot: 'bg-gray-400',
    bg: 'bg-gray-50 dark:bg-gray-900',
    text: 'text-gray-600 dark:text-gray-400',
    border: 'border-gray-200 dark:border-gray-700',
  },
};

/** Leesbaar bron-label voor een lead (incl. eventueel detail). */
/** Adres als "straat, plaats" (valt terug op wat beschikbaar is). */
export function formatLeadAddress(
  address: string | null | undefined,
  plaats: string | null | undefined,
): string {
  return [address, plaats].filter(Boolean).join(', ');
}

/** Duur van het aankomst-tijdsblok dat naar klanten wordt gecommuniceerd (uren). */
export const AFSPRAAK_TIJDSBLOK_UREN = 2;

/**
 * Klantcommunicatie toont een aankomst-tijdsblok ("tussen 11:30 en 13:30 uur")
 * in plaats van een harde tijd. Het blok ligt gecentreerd rond de geplande
 * (agenda)tijd: die geldt als verwachte aankomst; intern blijft hij leidend.
 */
export function formatTijdsblok(start: Date): string {
  const fmt = (d: Date) => d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  const halfBlokMs = (AFSPRAAK_TIJDSBLOK_UREN / 2) * 60 * 60 * 1000;
  const van = new Date(start.getTime() - halfBlokMs);
  const tot = new Date(start.getTime() + halfBlokMs);
  return `tussen ${fmt(van)} en ${fmt(tot)} uur`;
}
